import Cookies from "js-cookie";

const ACCESS_TOKEN_COOKIE_NAME = "sportsmet-access-token";
const REFRESH_TOKEN_COOKIE_NAME = "sportsmet-refresh-token";

// How long a written session cookie survives, in seconds. The same thirty days
// auth-api gives the refresh token itself (REFRESH_TOKEN_TTL_SECONDS in
// services/auth-api/src/lib/authConfig.ts), which auth-api re-stamps from the
// moment of use on every winning rotation.
//
// Without this the cookies below were SESSION cookies and died when the browser
// closed, so a signed-in fan reopening their laptop was signed out no matter
// what the server thought. With it, the expiry is recomputed on every refresh,
// which is what makes the browser's copy slide alongside the server's.
const SESSION_COOKIE_TTL_SECONDS = 60 * 60 * 24 * 30;

// A refresh leg must settle too: the same 60s-ceiling reasoning as call.ts,
// scaled down because this is a tiny request and it sits inside every other
// call's own ceiling.
const REFRESH_TIMEOUT_MS = 15000;

const env = (import.meta as { env?: Record<string, string | undefined> }).env;
function authHost(): string {
  const configuredHost = env?.VITE_AUTH_API_HOST;
  return configuredHost !== undefined && configuredHost !== ""
    ? configuredHost
    : "https://api.sportsmet.com/auth";
}

type RefreshSlot = { promise?: Promise<string | null> | undefined };
const globalSlots = globalThis as { __sportsmetAuthRefresh?: RefreshSlot };
globalSlots.__sportsmetAuthRefresh ??= {};
const slot: RefreshSlot = globalSlots.__sportsmetAuthRefresh;

// Read off globalThis, never a bare `window`: this file is compiled by web
// apps (DOM lib), by React Native (no DOM lib), and by a Bun service whose
// tsconfig lib is ES2022 only. A bare window identifier is a type error in
// the last two.
const browserLocation = (globalThis as { location?: { protocol?: string } })
  .location;
const isSecure = browserLocation?.protocol === "https:";

type LockManagerLike = {
  request?: (
    name: string,
    fn: () => Promise<string | null>,
  ) => Promise<string | null>;
};

function runExclusive(
  fn: () => Promise<string | null>,
): Promise<string | null> {
  const locks = (globalThis as { navigator?: { locks?: LockManagerLike } })
    .navigator?.locks;
  if (locks && typeof locks.request === "function") {
    return locks.request("sportsmet-auth-refresh", fn);
  }
  return fn();
}

// Single-flight per tab, plus a cross-tab Web Lock, so concurrent callers
// never rotate the refresh token twice: it has no grace window.
export default function refreshAccessToken(options?: {
  timeoutMs?: number;
}): Promise<string | null> {
  if (slot.promise) return slot.promise;
  const timeoutMs = Math.min(
    options?.timeoutMs ?? REFRESH_TIMEOUT_MS,
    REFRESH_TIMEOUT_MS,
  );
  slot.promise = runExclusive(async () => doRefresh(timeoutMs)).finally(() => {
    slot.promise = undefined;
  });
  return slot.promise;
}

async function doRefresh(timeoutMs: number): Promise<string | null> {
  const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE_NAME);
  if (!refreshToken) return null;

  const abortController =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  if (abortController) {
    timeoutHandle = setTimeout(() => {
      abortController.abort();
    }, timeoutMs);
  }
  try {
    // /session/refresh is what auth-api's public schema declares and what
    // mountSessionRoutes actually mounts. This template used to say
    // /token/refresh, a path that has never existed, so every refresh 404'd
    // and every session in every app died at access-token expiry. It failed
    // silently because a refused refresh is indistinguishable from an expired
    // session: the person just gets bounced to sign-in and blames the clock.
    const response = await fetch(`${authHost()}/v1/public/session/refresh`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify({ refreshToken }),
      ...(abortController ? { signal: abortController.signal } : {}),
    });
    const json = (await response.json()) as {
      error?: { code?: string } | null;
      data?: { accessToken?: string; refreshToken?: string } | null;
    } | null;
    const data = json?.data;
    // Only the server REFUSING this refresh token ends the session, and the
    // ERROR CODE in the envelope is what says so. The HTTP status is not
    // consulted, on purpose.
    //
    // auth-api answers all three refusals in postSessionRefreshHandler
    // (malformed, reuseDetected, refused) with code UNAUTHENTICATED, and
    // @sportsmet/errors puts UNAUTHENTICATED in the "forbidden" class, which
    // maps to HTTP 403. There is no 401 anywhere in @sportsmet/errors. The
    // only 401 in the platform comes from registerAuthMiddleware, the guard on
    // TOKEN-BEARING routes, and /session/refresh is public and carries no
    // bearer, so that guard never runs here.
    //
    // The status is a many-to-one projection of the code: "forbidden" also
    // covers FORBIDDEN, FORBIDDEN_POOL, FORBIDDEN_ROLE, FORBIDDEN_CHAT,
    // OAUTH_TOKEN_REJECTED and a failed bot check. A 403 therefore does not
    // mean "this refresh token is dead", and keying on it would end a live
    // session over an unrelated refusal. Reading the code is both exact and
    // stable: it is the value the status is derived FROM, so it keeps working
    // if the class-to-status map is ever changed again.
    //
    // Everything else is the call failing, not the token dying, so the cookies
    // stay and the next attempt can still succeed. Clearing on every non-2xx is
    // what signed fans out on a 429 from the per-IP rate limit on
    // /session/refresh and on a 502 from a rolling deploy, and it disagreed
    // with the catch below, which has always kept the cookies on a thrown
    // network error.
    const wasRefused = json?.error?.code === "UNAUTHENTICATED";
    if (wasRefused) {
      Cookies.remove(ACCESS_TOKEN_COOKIE_NAME);
      Cookies.remove(REFRESH_TOKEN_COOKIE_NAME);
      return null;
    }
    if (!response.ok || json?.error || !data?.accessToken) return null;
    // Computed here, per refresh, never hoisted: a hoisted expiry would freeze
    // the window at whenever this module was first evaluated, and a tab that has
    // been open for a week is not that.
    const cookieOptions = {
      sameSite: "strict",
      secure: isSecure,
      expires: new Date(Date.now() + SESSION_COOKIE_TTL_SECONDS * 1000),
    } as const;
    Cookies.set(ACCESS_TOKEN_COOKIE_NAME, data.accessToken, cookieOptions);
    if (data.refreshToken) {
      Cookies.set(REFRESH_TOKEN_COOKIE_NAME, data.refreshToken, cookieOptions);
    }
    return data.accessToken;
  } catch {
    return null;
  } finally {
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
  }
}
